import { useState } from "react";
import { useParams } from "react-router";
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Table,
  Tag,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  FolderOpenOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

import { useUser } from "@/contexts/UserContext";
import { useTrialPlans } from "@/hooks/useTrialPlans";
import type { ScenarioSummary } from "./types";

const dateFmt = (iso: string): string =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

interface NameModalState {
  mode: "new" | "copy" | "rename";
  scenario?: ScenarioSummary;
}

export default function TrialPlanningScenarios() {
  const { planId } = useParams<{ planId: string }>();
  const { userInfo } = useUser();
  const {
    getPlan,
    setActiveScenario,
    createScenario,
    renameScenario,
    deleteScenario,
    setScenarioApproval,
  } = useTrialPlans();
  const plan = planId ? getPlan(planId) : undefined;

  const [nameModal, setNameModal] = useState<NameModalState | null>(null);
  const [form] = Form.useForm<{ name: string }>();

  if (!plan) return null;

  const userName = [userInfo?.first_name, userInfo?.last_name]
    .filter(Boolean)
    .join(" ");
  const scenarios = plan.scenarioSummaries;

  const openNameModal = (state: NameModalState) => {
    setNameModal(state);
    form.setFieldsValue({
      name:
        state.mode === "rename"
          ? (state.scenario?.name ?? "")
          : state.mode === "copy"
            ? `${state.scenario?.name} (copy)`
            : `Scenario ${scenarios.length + 1}`,
    });
  };

  const handleNameOk = async () => {
    try {
      const { name } = await form.validateFields();
      if (!nameModal) return;
      if (nameModal.mode === "rename" && nameModal.scenario) {
        renameScenario(plan.id, nameModal.scenario.id, name);
        message.success(`Renamed to "${name}"`);
      } else {
        createScenario(plan.id, name, nameModal.scenario?.id, userName);
        message.success(`"${name}" created and opened`);
      }
      setNameModal(null);
      form.resetFields();
    } catch {
      // validation errors stay in the form
    }
  };

  const columns: ColumnsType<ScenarioSummary> = [
    {
      title: "Scenario",
      key: "name",
      render: (_, s) => (
        <button
          type="button"
          onClick={() => setActiveScenario(plan.id, s.id)}
          className="font-medium text-[13px] text-[#1F2933] hover:text-[#0C98D0] text-left transition-colors"
        >
          {s.name}
        </button>
      ),
    },
    {
      title: "Sites",
      key: "sites",
      width: 70,
      align: "center",
      render: (_, s) => (
        <span className="text-[13px] text-[#374151] tabular-nums">
          {s.siteCount}
        </span>
      ),
    },
    {
      title: "Created",
      key: "created",
      width: 160,
      render: (_, s) => (
        <div>
          <div className="text-[12px] text-[#374151]">
            {dateFmt(s.createdAt)}
          </div>
          <div className="text-[11px] text-[#8A97A3]">{s.createdBy || "—"}</div>
        </div>
      ),
    },
    {
      title: "Modified",
      key: "modified",
      width: 160,
      render: (_, s) => (
        <div>
          <div className="text-[12px] text-[#374151]">
            {dateFmt(s.updatedAt)}
          </div>
          <div className="text-[11px] text-[#8A97A3]">
            {s.updatedBy || s.createdBy || "—"}
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 170,
      render: (_, s) => (
        <span className="inline-flex gap-1">
          {s.id === plan.activeScenarioId && (
            <Tag color="blue" className="font-semibold">
              OPEN
            </Tag>
          )}
          {s.approvedAt ? (
            <Tag color="green" className="font-semibold">
              APPROVED
            </Tag>
          ) : (
            <Tag className="font-semibold text-[#6B7280]">DRAFT</Tag>
          )}
        </span>
      ),
    },
    {
      key: "actions",
      align: "right",
      width: 300,
      render: (_, s) => (
        <div className="flex gap-1.5 justify-end">
          {s.id !== plan.activeScenarioId && (
            <Button
              size="small"
              icon={<FolderOpenOutlined />}
              onClick={() => setActiveScenario(plan.id, s.id)}
            >
              Open
            </Button>
          )}
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openNameModal({ mode: "rename", scenario: s })}
          >
            Rename
          </Button>
          {s.approvedAt ? (
            <Button
              size="small"
              onClick={() => {
                setScenarioApproval(plan.id, s.id, false);
                message.success(`"${s.name}" back to draft`);
              }}
            >
              Unapprove
            </Button>
          ) : (
            <Popconfirm
              title="Approve this scenario?"
              description="Approval marks the scenario ready to publish and enables Add to Vivo. It should not be edited afterwards."
              okText="Approve"
              cancelText="Cancel"
              onConfirm={() => {
                setScenarioApproval(plan.id, s.id, true);
                message.success(
                  `"${s.name}" approved — Add to Vivo is enabled`,
                );
              }}
            >
              <Button size="small" icon={<CheckCircleOutlined />}>
                Approve
              </Button>
            </Popconfirm>
          )}
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={() => openNameModal({ mode: "copy", scenario: s })}
          >
            Copy
          </Button>
          <Popconfirm
            title="Delete this scenario?"
            description="The scenario and everything in it is removed. This cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={() => deleteScenario(plan.id, s.id)}
            disabled={scenarios.length <= 1}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={scenarios.length <= 1}
            >
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 mb-1 shrink-0">
        <h2 className="text-[15px] font-semibold text-[#1F2933] m-0">
          Scenarios
        </h2>
        <div className="flex-1" />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          className="bg-[#0C98D0] border-none"
          onClick={() => openNameModal({ mode: "new" })}
        >
          New Scenario
        </Button>
      </div>
      <p className="text-[12.5px] text-[#6B7280] max-w-[62ch] leading-relaxed mb-3 shrink-0">
        A scenario captures the entire plan at a point in time — recommended
        sites, selections, milestones, enrollment assumptions, and budget. Open
        a scenario to keep working on it; edits apply to the open scenario
        automatically. Copy one before a big change to keep a comparison point.
        Approving a scenario marks it ready to publish and enables Add to Vivo.
      </p>

      <div className="flex-1 min-h-0 rounded-[8px] border border-[#F0F2F4] bg-white overflow-hidden">
        <Table<ScenarioSummary>
          dataSource={scenarios}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={false}
          scroll={{ x: "max-content" }}
        />
      </div>

      <Modal
        title={
          nameModal?.mode === "rename"
            ? "Rename Scenario"
            : nameModal?.mode === "copy"
              ? "Copy Scenario"
              : "New Scenario"
        }
        open={!!nameModal}
        onCancel={() => {
          setNameModal(null);
          form.resetFields();
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setNameModal(null);
              form.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button
            key="ok"
            type="primary"
            className="bg-[#0C98D0] border-none"
            onClick={handleNameOk}
          >
            {nameModal?.mode === "rename" ? "Rename" : "Create"}
          </Button>,
        ]}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item
            name="name"
            label="Scenario Name"
            rules={[{ required: true, message: "Scenario name is required" }]}
          >
            <Input placeholder="e.g. Aug 18 NSCLC Ph2 · 50 sites · plan 1" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
